import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  Condition,
  PaginatedResponse,
  ServerPaginatedResponse,
} from "@/types/Api";
import {
  ManualValveReport,
  ReportId,
  SafetyValveReport,
} from "@/reports/types/SafetyValveReport";
import axios from "@/lib/axios";
import { Location } from "@/reports/config/locations";
import { SorterResult } from "antd/es/table/interface";
import { DIRECTIONS } from "@/constants/directions";
import {
  MANUAL_VALVE_REPORTS_API,
  REPORT_UPDATED_TIME_CHECK,
  SAFETY_VALVE_REPORTS_API,
  REPORT_SITES,
  VALVE_TEST_HISTORY_EXPORT,
} from "@/reports/config/api";
import { AxiosResponse } from "axios";
import { getASME } from "@/reports/utils/asme";
import { SECOND } from "@/constants/time";
import { ReportType } from "../types/Api";

interface SafetyValveReportParams {
  page?: number;
  limit?: number;
  search?: Condition[];
  location?: Location;
  sorter?: SorterResult<SafetyValveReport>;
}

export const getValveReports = async (
  params: SafetyValveReportParams,
  isViewer?: boolean,
  reportType: ReportType = "SAFETY_VALVE"
): Promise<ServerPaginatedResponse<SafetyValveReport | ManualValveReport>> => {
  const defaultSorter = params?.sorter?.order
    ? {
        direction: DIRECTIONS[params.sorter.order],
        column: params.sorter.columnKey,
      }
    : {
        direction: DIRECTIONS.DESC,
        column: "updatedAt",
      };

  const paramsWithDefaultSorter = {
    ...params,
    search: params.search ? JSON.stringify(params.search) : undefined,
    sort: JSON.stringify(defaultSorter),
  };

  if (reportType === "MANUAL_VALVE") {
    return axios.get(
      isViewer
        ? "/api/viewer/manualValveRepairReports"
        : "/api/manualValveRepairReport",
      {
        params: paramsWithDefaultSorter,
      }
    );
  }

  return axios.get(
    isViewer ? "/api/viewer/reports" : "/api/safetyValveReport",
    {
      params: paramsWithDefaultSorter,
    }
  );
};

export const useGetValveReports = ({
  params,
  isViewer,
  reportType = "SAFETY_VALVE",
  options,
}: {
  params: SafetyValveReportParams;
  isViewer?: boolean;
  reportType?: ReportType;
  options?: UseQueryOptions<
    ServerPaginatedResponse<SafetyValveReport | ManualValveReport>,
    Error,
    PaginatedResponse<SafetyValveReport | ManualValveReport>
  >;
}) =>
  useQuery({
    queryFn: async () => getValveReports(params, isViewer, reportType),
    queryKey: [
      reportType === "SAFETY_VALVE"
        ? SAFETY_VALVE_REPORTS_API
        : MANUAL_VALVE_REPORTS_API,
      "GET /",
      params,
      params.search,
      reportType,
      isViewer,
    ],
    select: (axiosResponse) => {
      return axiosResponse.data.data;
    },
    ...options,
  });

export const getSafetyValveReport = async (
  id: ReportId,
  location: Location,
  isViewer?: boolean
): Promise<AxiosResponse<{ data: SafetyValveReport }>> => {
  if (isViewer) {
    return axios.get(`/api/viewer/reports/${location}/${id.value}`);
  }
  return axios.get(`/api/safetyValveReport/${id.value}`, {
    params: { location },
  });
};

export const getManualValveReport = async (
  id: ReportId,
  location: Location,
  isViewer?: boolean
): Promise<AxiosResponse<{ data: ManualValveReport }>> => {
  if (isViewer) {
    return axios.get(
      `/api/viewer/manualValveRepairReports/${location}/${id.value}`
    );
  }

  return axios.get(`/api/manualValveRepairReport/${id.value}`, {
    params: { location },
  });
};

export const useGetValveReport = (
  id: ReportId,
  location: Location,
  isViewer?: boolean,
  reportType: ReportType = "SAFETY_VALVE"
) => {
  return useQuery({
    queryFn: async () =>
      reportType === "SAFETY_VALVE"
        ? getSafetyValveReport(id, location, isViewer)
        : getManualValveReport(id, location, isViewer),
    queryKey: [
      reportType === "SAFETY_VALVE"
        ? SAFETY_VALVE_REPORTS_API
        : MANUAL_VALVE_REPORTS_API,
      "GET /:id",
      id,
      location,
      isViewer,
      reportType,
    ],
    select: (response) => {
      if (reportType === "SAFETY_VALVE") {
        const report = response.data.data as SafetyValveReport;
        report.ASME = getASME(report);
        // Provide default values for these fields that used in Attachment tab
        report.NewAddedAttachments = [];
        report.DeletedAttachments = [];
        return report;
      }
      if (reportType === "MANUAL_VALVE") {
        const report = response.data.data as ManualValveReport;
        // Provide default values for these fields that used in Attachment tab
        report.NewAddedAttachments = [];
        report.DeletedAttachments = [];
        return report;
      }

      return response.data.data;
    },
    cacheTime: 0, // Disable cache to ensure we always get the latest data
  });
};

const checkReportUpdatedTime = async (
  id: number | string | undefined,
  location: Location,
  reportType: ReportType
): Promise<
  AxiosResponse<{
    data: {
      updatedAt: string | null;
      updatedBy: number | null;
      createdAt: string | null;
      createdBy: number | null;
    };
  }>
> => {
  if (reportType === "MANUAL_VALVE") {
    return axios.get(`/api/manualValveRepairReport/${id}/ping`, {
      params: { location },
    });
  }

  return axios.get(`/api/safetyValveReport/${id}/ping`, {
    params: { location },
  });
};

export const useCheckReportUpdatedTime = ({
  id,
  location,
  reportType,
  enabled = true,
}: {
  id: number | string | undefined;
  location: Location;
  reportType: ReportType;
  enabled?: boolean;
}) => {
  return useQuery({
    queryFn: async () => checkReportUpdatedTime(id, location, reportType),
    queryKey: [REPORT_UPDATED_TIME_CHECK, id, location, reportType],
    select: (response) => response.data.data,
    refetchInterval: 5 * SECOND, // Polling every 5 seconds to check if the report has been updated
    enabled: enabled && !!id,
    cacheTime: 0, // Disable cache to ensure we always get the latest data
  });
};

export const getReportSites = (location: Location) => {
  return axios.get<{
    data: {
      vCustomer: string | null;
    }[];
  }>(`/api/safetyValveReport/sites`, {
    params: { location },
  });
};

export const useGetReportSites = ({ location }: { location?: Location }) => {
  return useQuery({
    queryFn: async () => {
      if (!location)
        throw new Error("Location is needed to get the report sites");
      return getReportSites(location);
    },
    queryKey: [REPORT_SITES, location],
    select: (response) => response.data.data,
    enabled: !!location,
  });
};

export const getValveTestHistory = ({
  location,
  siteName,
  startDate,
}: {
  location: Location;
  siteName: string;
  startDate: string;
}) => {
  return axios.get<{ data: Partial<SafetyValveReport>[] }>(
    `/api/safetyValveReport/valve-test-history`,
    {
      params: {
        location,
        siteName,
        startDate,
      },
    }
  );
};

export const useGetValveTestHistory = ({
  location,
  siteName,
  startDate,
}: {
  location?: Location;
  siteName?: string;
  startDate?: string;
}) => {
  return useQuery({
    queryFn: async () => {
      if (!siteName) {
        throw new Error("Site name is needed to get the valve test history");
      }
      if (!startDate) {
        throw new Error("Start date is needed to get the valve test history");
      }
      if (!location) {
        throw new Error("Location is needed to get the valve test history");
      }
      return getValveTestHistory({ location, siteName, startDate });
    },
    queryKey: [VALVE_TEST_HISTORY_EXPORT, location, siteName, startDate],
    select: (response) => response.data.data,
    enabled: !!location && !!siteName && !!startDate,
  });
};
